import type React from 'react'
import { router } from '@inertiajs/react'

const ensureMinWaitTime = async (startTime: number, minWaitTime = 1000) => {
  const timeElapsed = Date.now() - startTime
  const remainingDelay = Math.max(0, minWaitTime - timeElapsed)

  return await new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, remainingDelay)
  })
}

export default function usePartialReload(
  queryString: string,
  partialReloadKeys: string[],
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  // Guard against `window` being unavailable. This hook fires a deferred
  // `router.get` on every render, so the resulting async Inertia navigation can
  // resolve and re-invoke the hook after a test's jsdom environment has been torn
  // down (where `window` no longer exists) — which otherwise surfaces as an
  // uncaught `ReferenceError: window is not defined` that fails the test run. It
  // also makes the hook safe under server-side rendering. In the browser `window`
  // is always present, so runtime behaviour is unchanged.
  if (typeof window === 'undefined') return

  const urlPath = window.location.pathname
  const url = queryString ? `${urlPath}?${queryString}` : urlPath
  const startTime = Date.now()

  // Work around for an issue with using partial reload inside of a useEffect
  // Inertia was loading once with undefined for a page name, breaking the render
  // https://github.com/inertiajs/inertia/issues/1547
  setTimeout(() => {
    router.get(
      url,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: partialReloadKeys,
        onStart: () => {
          setLoading(true)
        },
        onFinish: async () => {
          await ensureMinWaitTime(startTime)
          setLoading(false)
        },
      }
    )
  }, 0)
}
